import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import LuxuryInput from '../ui/LuxuryInput';
import { FaMapMarkerAlt, FaCheckCircle } from 'react-icons/fa';
import { formReveal } from '../animations/checkoutAnimations';

const AddressForm = ({
                         address,
                         onChange,
                         errors = {},
                         savedAddresses = [],
                         onSelectSavedAddress
                     }) => {
    const [selectedIndex, setSelectedIndex] = useState(null);

    const handleSavedAddressSelect = (savedAddress, index) => {
        setSelectedIndex(index);
        onChange(savedAddress);

        if (onSelectSavedAddress) {
            onSelectSavedAddress(savedAddress);
        }
    };

    // Helper to ensure address has all required fields
    const handleFieldChange = (field, value) => {
        const updatedAddress = { ...address, [field]: value };
        onChange(updatedAddress);
    };

    return (
        <motion.div
            variants={formReveal}
            initial="initial"
            animate="animate"
            className="space-y-6"
        >
            {/* Saved Addresses */}
            <AnimatePresence>
                {savedAddresses.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.4, ease: 'easeOut' }}
                        className="space-y-4 overflow-hidden"
                    >
                        <h3 className="font-medium text-charcoal">
                            Select Saved Address
                        </h3>

                        <div className="grid gap-3">
                            {savedAddresses.map((saved, index) => {
                                const isSelected = selectedIndex === index;

                                return (
                                    <motion.button
                                        key={index}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.08 }}
                                        onClick={() => handleSavedAddressSelect(saved, index)}
                                        className={`
                                            w-full p-4 text-left rounded-xl border transition-all
                                            ${
                                            isSelected
                                                ? 'border-champagne bg-champagne/5 ring-1 ring-champagne/30'
                                                : 'border-nude bg-white hover:border-champagne'
                                        }
                                        `}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-start gap-3">
                                                <div className="w-8 h-8 rounded-full bg-nude/30 flex items-center justify-center flex-shrink-0">
                                                    <FaMapMarkerAlt className="w-4 h-4 text-charcoal/50" />
                                                </div>
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="font-medium text-charcoal">
                                                            {saved.name}
                                                        </span>
                                                        {saved.isDefault && (
                                                            <span className="text-xs px-2 py-1 bg-sage/20 text-sage rounded-full">
                                                                Default
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-charcoal/60">
                                                        {saved.address}, {saved.city}, {saved.state} {saved.zipCode}
                                                    </p>
                                                    <p className="text-sm text-charcoal/50 mt-1">
                                                        📱 {saved.phone}
                                                        {saved.email && ` • ✉️ ${saved.email}`}
                                                    </p>
                                                </div>
                                            </div>
                                            {isSelected && (
                                                <FaCheckCircle className="w-5 h-5 text-champagne flex-shrink-0" />
                                            )}
                                        </div>
                                    </motion.button>
                                );
                            })}
                        </div>

                        <div className="pt-4 border-t border-nude/30">
                            <p className="text-center text-sm text-charcoal/60 mb-3">
                                Or continue with a new address
                            </p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* New Address Form */}
            <div className="space-y-4">
                <h3 className="font-medium text-charcoal">
                    {savedAddresses.length > 0 ? 'Enter New Address' : 'Shipping Address'}
                </h3>

                <div className="grid md:grid-cols-2 gap-4">
                    <LuxuryInput
                        label="Full Name"
                        placeholder="Enter full name"
                        value={address.name || ''}
                        onChange={(e) => handleFieldChange('name', e.target.value)}
                        error={errors.name}
                        required
                        size="large"
                    />

                    <LuxuryInput
                        type="tel"
                        label="Phone Number"
                        placeholder="Enter phone number"
                        value={address.phone || ''}
                        onChange={(e) => handleFieldChange('phone', e.target.value)}
                        error={errors.phone}
                        required
                        size="large"
                    />
                </div>

                <LuxuryInput
                    type="email"
                    label="Email Address"
                    placeholder="Enter email address"
                    value={address.email || ''}
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                    error={errors.email}
                    required
                    size="large"
                />

                <LuxuryInput
                    label="Street Address"
                    placeholder="House number, street, locality"
                    value={address.address || ''}
                    onChange={(e) => handleFieldChange('address', e.target.value)}
                    error={errors.address}
                    required
                    size="large"
                    multiline
                    rows={2}
                />

                <div className="grid md:grid-cols-3 gap-4">
                    <LuxuryInput
                        label="City"
                        placeholder="City"
                        value={address.city || ''}
                        onChange={(e) => handleFieldChange('city', e.target.value)}
                        error={errors.city}
                        required
                        size="large"
                    />

                    <LuxuryInput
                        label="State"
                        placeholder="State"
                        value={address.state || ''}
                        onChange={(e) => handleFieldChange('state', e.target.value)}
                        error={errors.state}
                        required
                        size="large"
                    />

                    <LuxuryInput
                        label="PIN Code"
                        placeholder="6-digit PIN"
                        value={address.zipCode || ''}
                        onChange={(e) => handleFieldChange('zipCode', e.target.value)}
                        error={errors.zipCode}
                        required
                        size="large"
                        maxLength={6}
                    />
                </div>

                <LuxuryInput
                    label="Country"
                    placeholder="Country"
                    value={address.country || 'India'}
                    onChange={(e) => handleFieldChange('country', e.target.value)}
                    error={errors.country}
                    required
                    size="large"
                    disabled
                />
            </div>
        </motion.div>
    );
};

export default AddressForm;